import { useLocation } from "react-router";
import type { UsageDto } from "@/shared/api/types";
import {
  CORE_WORKSPACE_ID,
  NAV_ITEMS,
  ORG_LEVEL_ITEM_IDS,
  WORKSPACES,
  workspaceForPath,
  workspaceHome,
  type NavItem,
  type Workspace,
} from "@/shared/constants/nav";
import { useEntitlementsQuery } from "./useEntitlements";
import { usePermissions } from "./usePermissions";

export interface ResolvedWorkspace {
  workspace: Workspace;
  /** The entries this caller may actually see; a workspace with none of them is not offered at all. */
  items: readonly NavItem[];
  /** Where choosing it in the switcher lands: its first navigable entry. */
  home: string;
}

export interface WorkspaceNav {
  /** The workspace the URL is in — the one the sidebar is showing. */
  current: ResolvedWorkspace;
  /** Core first, then every live module: what the switcher offers. */
  available: readonly ResolvedWorkspace[];
  /**
   * False for a tenant with no add-ons, and then the switcher is not rendered at all (ADR-0017
   * second addendum): it must never become a standing reminder of what they have not bought.
   */
  showSwitcher: boolean;
  /**
   * Whether the switcher's menu ends with "Add modules…" (ADR-0017 addendum of 2026-10-01, second): the
   * caller may manage the subscription — `billing:manage`, the gate on the trial route and on every
   * "Try free" and "Change plan" — and at least one module is not held. One item behind a click, never a
   * drawn module: the switcher itself still lists only what the organisation has.
   */
  addModules: boolean;
  /**
   * Settings, which follows the user into a module workspace because "what we have bought, who may
   * sign in, what a role allows" are questions about the ORGANISATION rather than about the product
   * in front of them. Empty inside Core, where it is already one of the entries.
   *
   * One entry, not two, since the redesign folded Members & roles into Settings tabs (pass 01 board
   * `1f`) — `ORG_LEVEL_ITEM_IDS` is the list and this sentence used to name the pre-redesign pair.
   */
  orgLevelItems: readonly NavItem[];
}

/** One module workspace, whether or not the organisation holds it: what Settings → Modules lists. */
export interface ModuleWorkspace extends ResolvedWorkspace {
  /** Its add-on is live — bought or on trial — so the switcher offers it. */
  held: boolean;
}

function resolve(workspace: Workspace, allowed: (item: NavItem) => boolean): ResolvedWorkspace {
  const items = workspace.items.filter(allowed);
  return { workspace, items, home: workspaceHome(workspace, items) };
}

/**
 * Bought or on trial, read off the entitlements' feature flags — the ONE reading of "held" the switcher, its
 * "Add modules…" item and Settings → Modules share. Core has no add-on and is always held.
 */
function isHeld(workspace: Workspace, usage: UsageDto | undefined): boolean {
  return workspace.planFeature ? usage?.features[workspace.planFeature] === true : true;
}

/** Whether this caller may see an entry: its role gate, the one rule both hooks below apply. */
function useAllowed(): (item: NavItem) => boolean {
  const { can } = usePermissions();
  return (item) => !item.permission || can(item.permission.module, item.permission.action);
}

/**
 * The sidebar's whole state, derived and never stored (ADR-0017 second addendum). The current
 * workspace comes from the URL alone, which is what makes a deep link select the right one, a
 * refresh keep it and the back button walk out of it — the three things a remembered "last module"
 * gets wrong, and the reason this is a `useLocation` and not a `useState`.
 */
export function useWorkspaces(): WorkspaceNav {
  const { pathname } = useLocation();
  const { can } = usePermissions();
  const usage = useEntitlementsQuery();
  const allowed = useAllowed();

  const current = resolve(workspaceForPath(pathname), allowed);
  // Bought (or on trial) AND with something in it this role may open. Both, or it is not offered:
  // a dead entry for an add-on nobody holds and an empty menu are the same disappointment.
  const available = WORKSPACES.filter((workspace) => isHeld(workspace, usage.data))
    .map((workspace) => resolve(workspace, allowed))
    .filter((entry) => entry.items.length > 0);

  // The workspace the URL is in is always offered, even when the add-on lapsed mid-session — the
  // page behind it says so itself, and hiding the way back out would strand the reader.
  const offered = available.some((entry) => entry.workspace.id === current.workspace.id)
    ? available
    : [...available, current];

  const modules = offered.filter((entry) => entry.workspace.id !== CORE_WORKSPACE_ID);
  const inCore = current.workspace.id === CORE_WORKSPACE_ID;

  return {
    current,
    available: offered,
    showSwitcher: modules.length > 0,
    // Only once the entitlements have answered: before that every module would read as not held.
    // A workspace that comes with the base plan (`baseIncluded`, M24 Face Kiosk) is never one to add.
    addModules:
      usage.data !== undefined &&
      can("billing", "manage") &&
      WORKSPACES.some((workspace) => !workspace.baseIncluded && !isHeld(workspace, usage.data)),
    orgLevelItems: inCore
      ? []
      : NAV_ITEMS.filter((item) => ORG_LEVEL_ITEM_IDS.includes(item.id) && allowed(item)),
  };
}

/**
 * Every MODULE workspace, held or not, in the switcher's order (ADR-0017 addendum of 2026-10-01, second):
 * what Settings → Modules lists. `home` is where "Open" lands for this role; a workspace in which the role
 * may open nothing has no items, and the page offers no "Open" for it.
 */
export function useModuleWorkspaces(): readonly ModuleWorkspace[] {
  const usage = useEntitlementsQuery();
  const allowed = useAllowed();
  return WORKSPACES.filter((workspace) => workspace.id !== CORE_WORKSPACE_ID).map((workspace) => ({
    ...resolve(workspace, allowed),
    held: isHeld(workspace, usage.data),
  }));
}
